import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { markExplored } from "@/lib/markExplored";
import { money, fmtDate } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import { useFilters } from "@/context/FiltersContext";
import { useAuth } from "@/context/AuthContext";
import { RODEO_EVENTS } from "@/components/Onboarding";
import MyResults from "@/components/MyResults";
import SeasonCommand from "@/components/SeasonCommand";
import CpraConnect from "@/components/CpraConnect";
import PrcaConnect from "@/components/PrcaConnect";
import HomePicker from "@/components/HomePicker";
import { toast } from "sonner";
import { Trophy, Search, X, ChevronDown, ChevronRight, Loader2, UserPlus, MapPin, Crosshair, Home, Map as MapIcon } from "lucide-react";

function AccountConnect({ m }) {
  const [acct, setAcct] = useState("CPRA");
  return (
    <section>
      <div className={`text-[11px] font-bold tracking-wider uppercase mb-2 ${m ? "text-slate-500" : "text-slate-400"}`}>My Association Accounts</div>
      <div className="flex gap-1.5 mb-3" data-testid="season-account-tabs">
        {[{ k: "CPRA", c: "#C8000F" }, { k: "PRCA", c: "#003087" }].map((a) => (
          <button key={a.k} onClick={() => setAcct(a.k)} data-testid={`season-account-${a.k}`}
            className={`text-sm font-bold px-4 py-2 rounded-lg transition-colors ${acct === a.k ? "text-white" : m ? "bg-white/5 text-slate-300" : "bg-[#F1F3F5] text-slate-600 hover:bg-[#E4E7EB]"}`}
            style={acct === a.k ? { background: a.c } : {}}>{a.k}</button>
        ))}
      </div>
      {acct === "CPRA" ? <CpraConnect m={m} /> : <PrcaConnect m={m} />}
    </section>
  );
}

function CompetitorSearch({ m, placeholder, onPick }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/competitors/search?q=${encodeURIComponent(q.trim())}`);
        setResults(data);
      } catch { /* ignore */ } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const inp = m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400";
  return (
    <div className="relative">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid="competitor-search-input"
          className={`w-full rounded-lg border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${inp}`} />
        {loading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-[#C8000F]" />}
      </div>
      {results.length > 0 && (
        <div className={`absolute z-20 mt-1 w-full rounded-lg border shadow-lg overflow-hidden ${m ? "bg-[#161B22] border-white/10" : "bg-white border-[#E4E7EB]"}`} data-testid="competitor-search-results">
          {results.map((r) => (
            <button key={r.name} onClick={() => { onPick(r); setQ(""); setResults([]); }} data-testid={`competitor-result-${r.name.replace(/\s/g, "-")}`}
              className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 ${m ? "hover:bg-white/5 text-slate-200" : "hover:bg-[#FAFBFC]"}`}>
              <span className="font-medium text-sm">{r.name}</span>
              {r.hometown && <span className="text-xs text-slate-400 truncate">{r.hometown}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FeedCard({ m, name, onUnfollow }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open && !data) api.get(`/competitors/feed?name=${encodeURIComponent(name)}`).then((r) => setData(r.data)).catch(() => {});
  }, [open, data, name]);

  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  return (
    <div className={`rounded-xl border ${card}`} data-testid={`following-card-${name.replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 p-4">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 flex-1 text-left">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <span className={`font-display font-bold ${m ? "text-slate-100" : "text-[#14181F]"}`}>{name}</span>
        </button>
        <button onClick={() => onUnfollow(name)} data-testid={`unfollow-${name.replace(/\s/g, "-")}`}
          className="text-xs font-semibold text-slate-400 hover:text-[#C8000F] flex items-center gap-1"><X className="w-3.5 h-3.5" /> Unfollow</button>
      </div>
      {open && (
        <div className="px-4 pb-4">
          {!data ? <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div> : (
            <>
              {data.standings.length > 0 && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {data.standings.map((s) => (
                    <span key={s.event} className={`text-[11px] px-2 py-1 rounded-md ${m ? "bg-[#C8000F]/15 text-[#F26D77] border border-[#C8000F]/30" : "bg-[#FBF4F5] text-[#9E0008] border border-[#C8000F]/20"}`}>
                      {s.event}: #{s.rank} · {money(s.earnings)}
                    </span>
                  ))}
                </div>
              )}
              {data.entries.length === 0 ? (
                <div className="text-sm text-slate-400 py-3">Not drawn into any posted rodeos yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {data.entries.map((e, i) => (
                    <button key={i} onClick={() => nav(`/rodeo/${e.rodeo_id}`)} data-testid={`feed-entry-${i}`}
                      className={`w-full text-left flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm ${m ? "hover:bg-white/5" : "hover:bg-[#FAFBFC] border border-transparent hover:border-[#EEF0F2]"}`}>
                      <div className="min-w-0">
                        <div className={`font-medium ${m ? "text-slate-100" : "text-[#14181F]"}`}>{e.rodeo_name} <span className="text-slate-400 font-normal">· {e.event}</span></div>
                        <div className="text-xs text-slate-400">
                          {e.performance_label} {e.performance_date ? `· ${fmtDate(e.performance_date)}` : ""} · draw #{e.position}
                          {e.role ? ` · ${e.role}` : ""}{e.partner ? ` w/ ${e.partner}` : ""}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function Season() {
  const m = useIsMobile();
  const { events, setEventsList } = useFilters();
  const toggleMyEvent = (e) => {
    const next = new Set(events); next.has(e) ? next.delete(e) : next.add(e);
    const arr = [...next]; setEventsList(arr);
    api.post("/me/events", { events: arr }).catch(() => {});
  };
  const [myName, setMyName] = useState(undefined);   // undefined=loading, null=not set, string=set
  const [myEvents, setMyEvents] = useState([]);
  const [editingMe, setEditingMe] = useState(false);
  const [following, setFollowing] = useState([]);
  const [home, setHome] = useState(null);
  const [homeInput, setHomeInput] = useState("");
  const [homeSaving, setHomeSaving] = useState(false);
  const [pickingHome, setPickingHome] = useState(false);
  const { user, refreshUser } = useAuth();
  const [emailInput, setEmailInput] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);
  const saveEmail = async () => {
    const e = emailInput.trim().toLowerCase();
    if (!e || e === (user?.email || "").toLowerCase()) return;
    setEmailSaving(true);
    try {
      await api.post("/me/email", { email: e });
      if (refreshUser) await refreshUser();
      setEmailInput("");
      toast.success("Email updated");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setEmailSaving(false); }
  };

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/me/standings");
      setMyName(data.competitor_name || null);
      setMyEvents(data.events || []);
      setEditingMe(!data.competitor_name);
    } catch { setMyName(null); }
  }, []);
  const loadFollowing = useCallback(() => api.get("/competitors/following").then((r) => setFollowing(r.data)).catch(() => {}), []);
  const loadHome = useCallback(() => api.get("/me/home").then((r) => setHome(r.data)).catch(() => {}), []);

  useEffect(() => { markExplored("ai_coach"); loadMe(); loadFollowing(); loadHome(); }, [loadMe, loadFollowing, loadHome]);

  const saveHome = async () => {
    if (homeInput.trim().length < 2) return;
    setHomeSaving(true);
    try { const { data } = await api.post("/me/home", { town: homeInput.trim() }); setHome(data); setHomeInput(""); toast.success(`Home base set to ${data.town}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setHomeSaving(false); }
  };

  const pickMe = async (r) => {
    try { await api.post("/me/competitor", { name: r.name }); setEditingMe(false); await loadMe(); toast.success(`Tracking ${r.name}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const follow = async (r) => {
    if (following.includes(r.name)) return toast.info(`Already following ${r.name}`);
    try { await api.post("/competitors/follow", { name: r.name }); await loadFollowing(); toast.success(`Following ${r.name}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const unfollow = async (name) => {
    try { await api.delete(`/competitors/follow?name=${encodeURIComponent(name)}`); await loadFollowing(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const title = m ? "text-slate-100" : "text-[#14181F]";

  return (
    <div className={page} data-testid="season-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center gap-2 ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <Trophy className="w-6 h-6 text-[#C8000F]" />
        <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>My Season</h1>
      </div>

      <div className="p-4 sm:p-6 max-w-3xl space-y-8">
        {/* Account / email */}
        <section>
          <div className={`text-[11px] font-bold tracking-wider uppercase mb-2 ${m ? "text-slate-500" : "text-slate-400"}`}>Account</div>
          <div className={`rounded-xl border p-5 ${card}`} data-testid="account-card">
            <div className={`text-sm ${title}`}>You're signed in as</div>
            <div className={`font-display font-bold text-lg mb-3 ${title}`} data-testid="account-email">{user?.email || "—"}</div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Change email</div>
            <div className="flex gap-2">
              <input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveEmail()}
                type="email" placeholder="new@email.com" data-testid="account-email-input"
                className={`flex-1 rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400"}`} />
              <button onClick={saveEmail} disabled={emailSaving || !emailInput.trim()} data-testid="account-email-save"
                className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
                {emailSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Update
              </button>
            </div>
          </div>
        </section>

        {/* Home base */}
        <section>
          <div className={`text-[11px] font-bold tracking-wider uppercase mb-2 ${m ? "text-slate-500" : "text-slate-400"}`}>Home Base</div>
          <div className={`rounded-xl border p-5 ${card}`} data-testid="home-base-card">
            <div className={`flex items-center gap-2 font-display font-bold mb-1 ${title}`}><Home className="w-4 h-4 text-[#C8000F]" /> Where do you roll out from?</div>
            <p className="text-sm text-slate-400 mb-3">
              {home?.town ? <>Your home base is <b className={title}>{home.town}</b>. Map distances, drive times and the run planner all measure from here.</> : "Set your home town so the map and planner measure real distances from your place."}
            </p>
            <div className="flex gap-2">
              <input value={homeInput} onChange={(e) => setHomeInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveHome()}
                placeholder={home?.town ? `Change from ${home.town}…` : "e.g. Ponoka, AB"} data-testid="home-input"
                className={`flex-1 rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400"}`} />
              <button onClick={saveHome} disabled={homeSaving || homeInput.trim().length < 2} data-testid="save-home-btn"
                className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white text-sm font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50">
                {homeSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Home className="w-4 h-4" />} Set
              </button>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <div className={`flex-1 h-px ${m ? "bg-white/10" : "bg-[#EEF0F2]"}`} />
              <span className="text-[11px] text-slate-400 uppercase tracking-wide">or</span>
              <div className={`flex-1 h-px ${m ? "bg-white/10" : "bg-[#EEF0F2]"}`} />
            </div>
            <button onClick={() => setPickingHome(true)} data-testid="pick-home-on-map-btn"
              className={`mt-3 w-full flex items-center justify-center gap-2 text-sm font-semibold px-4 py-2.5 rounded-lg border ${m ? "border-white/10 text-slate-200 hover:bg-white/5" : "border-[#D7DBE0] text-slate-700 hover:bg-[#FAFBFC]"}`}>
              <MapIcon className="w-4 h-4 text-[#E68A2E]" /> Pick a point on the map
            </button>
          </div>
        </section>

        {/* Association accounts — CPRA + PRCA connect side by side */}
        <AccountConnect m={m} />

        {/* My events */}
        <section>
          <div className={`text-[11px] font-bold tracking-wider uppercase mb-2 ${m ? "text-slate-500" : "text-slate-400"}`}>My Events</div>
          <div className={`rounded-xl border p-5 ${card}`} data-testid="my-events-card">
            <p className="text-sm text-slate-400 mb-3">Pick the events you compete in — draws default to showing just these.</p>
            <div className="flex flex-wrap gap-2">
              {RODEO_EVENTS.map((e) => {
                const on = events.has(e);
                return (
                  <button key={e} onClick={() => toggleMyEvent(e)} data-testid={`my-event-toggle-${e.replace(/\s/g, "-")}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-md border ${on ? "bg-[#C8000F] text-white border-[#C8000F]" : (m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#D7DBE0] hover:border-slate-400")}`}>{e}</button>
                );
              })}
            </div>
          </div>
        </section>

        {/* My Season — strategy command center */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <div className={`text-[11px] font-bold tracking-wider uppercase ${m ? "text-slate-500" : "text-slate-400"}`}>My Season</div>
            {myName && !editingMe && <button onClick={() => setEditingMe(true)} data-testid="change-me-btn" className="text-xs font-semibold text-[#C8000F] hover:underline">Not {myName}?</button>}
          </div>
          {myName === undefined ? (
            <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C8000F]" /></div>
          ) : editingMe ? (
            <div className={`rounded-xl border p-5 ${card}`}>
              <div className={`flex items-center gap-2 font-display font-bold mb-1 ${title}`}><Crosshair className="w-4 h-4 text-[#C8000F]" /> Who are you?</div>
              <p className="text-sm text-slate-400 mb-3">Find your name in the standings and we'll track your rank, earnings, gap to #1 and your path to the CFR — plus an AI coach that plans your season.</p>
              <CompetitorSearch m={m} placeholder="Search your name…" onPick={pickMe} />
              {myName && <button onClick={() => setEditingMe(false)} className="mt-3 text-xs text-slate-400 hover:underline">Cancel</button>}
            </div>
          ) : (
            <SeasonCommand m={m} />
          )}
        </section>

        {/* My results — log time/score/$ won, unofficial → official */}
        <MyResults m={m} />

        {/* Follow competitors */}
        <section>
          <div className={`text-[11px] font-bold tracking-wider uppercase mb-2 ${m ? "text-slate-500" : "text-slate-400"}`}>Follow Competitors</div>
          <div className={`rounded-xl border p-5 mb-4 ${card}`}>
            <div className={`flex items-center gap-2 font-display font-bold mb-1 ${title}`}><UserPlus className="w-4 h-4 text-[#C8000F]" /> Track a rival or travel partner</div>
            <p className="text-sm text-slate-400 mb-3">Follow anyone to see every rodeo they're drawn into and their season standings.</p>
            <CompetitorSearch m={m} placeholder="Search a competitor…" onPick={follow} />
          </div>
          {following.length === 0 ? (
            <div className={`rounded-xl border border-dashed p-8 text-center text-sm ${m ? "border-white/15 text-slate-400" : "border-[#D7DBE0] text-slate-500"}`}>
              <MapPin className="w-5 h-5 mx-auto mb-2 opacity-60" />
              You're not following anyone yet.
            </div>
          ) : (
            <div className="space-y-3">
              {following.map((name) => <FeedCard key={name} m={m} name={name} onUnfollow={unfollow} />)}
            </div>
          )}
        </section>
      </div>

      <HomePicker m={m} open={pickingHome} onClose={() => setPickingHome(false)}
        initial={home?.lat ? { lat: home.lat, lng: home.lng } : null}
        onDone={(data) => setHome(data)} />
    </div>
  );
}
